import superPlanVideo from '@assets/video/super-plan-intro.mp4'
import { superBrandGradient } from '@/customTheme'
import type { PlanType } from './MetsiKonChoosePlan'
import { AI_MODELS_HD_SOURCE } from '@/scenes/engine/utils/aiModelsAnalytics'
import { BATCH_AI_TOOLS_SUPER_SOURCES } from '@/scenes/BatchStudio/analytics/batchAIToolsAnalytics'
import { BATCH_IMAGE_LIMIT_DEFAULT, BATCH_IMAGE_LIMIT_SUPER } from '@/scenes/BatchStudio/batchImageLimit'

export type { PlanType }

export interface PlanFeature {
  key: string
  options?: Record<string, unknown>
}

export const PREMIUM_FEATURES: PlanFeature[] = [
  { key: 'Unlock advanced AI tools' },
  { key: '250 AI credits monthly' },
  { key: 'Batch edit up to {{max}} photos at once', options: { max: BATCH_IMAGE_LIMIT_DEFAULT } },
  { key: 'Create on-model and studio product shots' },
  { key: 'Remove people and objects' },
  { key: 'Access all content and editing tools' },
  { key: 'Save without watermark' },
]

export const SUPER_FEATURES: PlanFeature[] = [
  { key: 'Everything in Premium' },
  { key: 'Batch edit up to {{max}} photos at once', options: { max: BATCH_IMAGE_LIMIT_SUPER } },
  { key: 'Unlock AI video' },
  { key: 'Best AI models (nano banana, GPT image)' },
  { key: '4x more AI credits' },
  { key: 'Generate on-model & product shots in HD' },
  { key: 'VIP support' },
]

interface PlanVariantTokens {
  checkColor: string
  choosePlanGradient: string
  introButtonGradient: string | null
  introVideo: string | null
  features: readonly PlanFeature[]
}

const PLAN_VARIANTS: Record<PlanType, PlanVariantTokens> = {
  premium: {
    checkColor: '#FF0560',
    choosePlanGradient:
      'linear-gradient(0deg, rgba(251, 248, 255, 0.10) 0%, rgba(255, 5, 96, 0.15) 100%)',
    introButtonGradient: null,
    introVideo: null,
    features: PREMIUM_FEATURES,
  },
  super: {
    checkColor: '#9d01f4',
    choosePlanGradient:
      'linear-gradient(0deg, rgba(251, 248, 255, 0.10) 0%, rgba(157, 1, 244, 0.15) 100%)',
    introButtonGradient: superBrandGradient,
    introVideo: superPlanVideo,
    features: SUPER_FEATURES,
  },
}

export function getPlanVariant(planType: PlanType): PlanVariantTokens {
  return PLAN_VARIANTS[planType]
}

/**
 * Source used by the post-purchase limited-time Super offer (ModalLimitedTimeOffer).
 * Its CTA changes the existing subscription's price in place rather than opening
 * the paywall, so this source only tags analytics; it stays listed in
 * SUPER_INITIAL_SOURCES / METSIKON_SOURCES so that any future paywall entry from
 * the offer still lands on the Super pricing screen.
 */
export const LIMITED_TIME_OFFER_SOURCE = 'BtSubscriptionSuperLimitedOffer'

/**
 * Source for the paywall opened from the free-user limited offer's CTA. It opens
 * on Premium and carries the offer's Paddle discount. Must stay in
 * `METSIKON_SOURCES` (Metsikon.tsx) so the plan chooser opens directly.
 */
export const LIMITED_OFFER_PREMIUM_SOURCE = 'BtSubscriptionLimitedOffer'

/**
 * Source for the "Upgrade to Super" CTA shown in the Batch Studio photo-limit modal.
 * Must also stay in `METSIKON_SOURCES` (Metsikon.tsx) — sources missing from that set
 * do not auto-open the plan chooser.
 */
export const BATCH_LIMIT_SOURCE = 'BtSubscriptionBatchLimit'

/**
 * Source for "Upgrade to Super" in the AI credits menu. Opens on Super with the
 * plan chooser, like the header button, but keeps its own analytics source.
 * Must also stay in `METSIKON_SOURCES` (Metsikon.tsx).
 */
export const AI_CREDITS_MENU_SUPER_SOURCE = 'BtSubscriptionAICreditsMenu'

/** Sources that open the paywall on the Super tier instead of Premium. */
export const SUPER_INITIAL_SOURCES = new Set<string>([
  'Header_CTA_Super',
  AI_CREDITS_MENU_SUPER_SOURCE,
  'BtSubscriptionOutOfCredits',
  'BtSubscriptionAIHD',
  AI_MODELS_HD_SOURCE,
  'BtSubscriptionAIVideo',
  // AI-video feed items are `isSuper` Magic Templates; iOS uses this paywall source.
  'BtSubscriptionCustomModels',
  ...BATCH_AI_TOOLS_SUPER_SOURCES,
  LIMITED_TIME_OFFER_SOURCE,
  BATCH_LIMIT_SOURCE,
])

export function resolveInitialPlanType(source: string | null | undefined): PlanType {
  if (source && SUPER_INITIAL_SOURCES.has(source)) return 'super'
  return 'premium'
}

export function getIntroTitleKey(planType: PlanType): string {
  return planType === 'super' ? 'Upgrade to Super' : 'Try Bazaart Premium'
}

export function getIntroSubtitleKey(planType: PlanType): string {
  return planType === 'super'
    ? 'Best for growing online sellers and brands.'
    : 'Give yourself extra design superpowers!'
}

export function getIntroCtaKey(
  planType: PlanType,
  userHadFreeTrial: boolean | undefined,
): string {
  if (planType === 'super') return 'Upgrade to Super'
  return userHadFreeTrial ? 'Try it now' : 'Try it free'
}
